import React, { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Col, Container, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { AiOutlineDelete } from 'react-icons/ai';
import { IoAddOutline, IoEyeOutline, IoFilterOutline, IoTrashOutline } from 'react-icons/io5';
import { toast } from 'react-toastify';
import ReactDatePicker from 'react-datepicker';
import CreateNarrativeReportModal from './components/CreateNarrativeReportModal';
import ViewReportModal from './components/ViewReportModal';
import { createNarrativeReportApi, deleteNarrativeReportApi, fetchNarrativeReportsApi, getNarrativeReportApi } from './narrativeReportsApi';
import { REPORT_STATUSES, REPORT_TYPE_FILTERS } from './narrativeReportsConfig';
import DataTable from '../../components/DataTable';
import { datePickerClass, parseUserInfo } from '../../utilities/common';
import { formatUserName } from '../reports/reportHelper';

const monthStringToDate = (value) => {
  if (!value) return null;
  const [year, month] = value.split('-');
  return new Date(Number(year), Number(month) - 1, 1);
};

const dateToMonthString = (value) => {
  if (!value) return '';
  const year = value.getFullYear();
  const month = `${value.getMonth() + 1}`.padStart(2, '0');
  return `${year}-${month}`;
};

const monthPickerClass = (invalid = false) => {
  const classProps = datePickerClass(invalid);
  return {
    ...classProps,
    wrapperClassName: `${classProps.wrapperClassName} mr-2 mb-2 narrative-month-picker`,
  };
};

const NarrativeReportsTab = () => {
  const { t } = useTranslation();
  const match = useRouteMatch();
  const history = useHistory();
  const { orgId, usrTimezone } = parseUserInfo(useSelector((state) => state.users.user));
  const [reports, setReports] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [cloneFramework, setCloneFramework] = useState(null);
  const [cloneSourceId, setCloneSourceId] = useState(null);
  const [viewReport, setViewReport] = useState(null);
  const [isViewLoading, setIsViewLoading] = useState(false);
  const [deleteReport, setDeleteReport] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [frameworkFilter, setFrameworkFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [startFilter, setStartFilter] = useState('');
  const [endFilter, setEndFilter] = useState('');

  useEffect(() => {
    fetchNarrativeReportsApi(orgId)
      .then((res) => setReports(res.data ?? res))
      .catch(() => { });
  }, [orgId]);

  const filteredReports = useMemo(() => {
    if (!reports) return [];
    return reports.filter((report) => {
      const matchFramework = frameworkFilter === 'all'
        || report.framework === frameworkFilter
        || (frameworkFilter === 'TCFD' && report.framework === 'TCFD-SAMPLE-2');
      const matchStatus = statusFilter === 'all' || report.status === statusFilter;
      const matchStart = !startFilter || report.startDate >= startFilter;
      const matchEnd = !endFilter || report.endDate <= endFilter;
      return matchFramework && matchStatus && matchStart && matchEnd;
    });
  }, [reports, frameworkFilter, statusFilter, startFilter, endFilter]);

  const handleCreate = async (payload) => {
    try {
      setIsCreating(true);
      const report = await createNarrativeReportApi(orgId, { ...payload, sourceReportId: cloneSourceId }, usrTimezone);
      setReports((items) => [report, ...(items || [])]);
      setShowCreate(false);
      setCloneFramework(null);
      setCloneSourceId(null);
      toast.success(t('Narrative report created'));
      history.push(`${match.url}/${report.id}`);
    } catch (e) {
      toast.error(t('Failed to create narrative report'));
    } finally {
      setIsCreating(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteNarrativeReportApi(orgId, deleteReport.id);
      setReports((items) => items.filter((report) => report.id !== deleteReport.id));
      setDeleteReport(null);
      toast.success(t('Report deleted successfully'));
    } catch (e) {
      toast.error(t('Failed to delete narrative report'));
    }
  };

  const clearFilters = () => {
    setFrameworkFilter('all');
    setStatusFilter('all');
    setStartFilter('');
    setEndFilter('');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getReportTypeLabel = (framework) => {
    if (framework === 'TCFD-SAMPLE-2') return 'TCFD';
    return framework;
  };

  const columns = useMemo(
    () => [
      {
        Header: t('Report Name'),
        accessor: 'name',
        Cell: ({ row }) => <span className="font-weight-bold">{row.original.name}</span>,
        sortType: 'string',
      },
      {
        Header: t('Time Period'),
        accessor: 'reportingPeriod',
        Cell: ({ row }) => <span className="text-muted">{row.original.reportingPeriod}</span>,
        sortType: 'string',
      },
      {
        Header: t('Report Type'),
        accessor: 'framework',
        Cell: ({ row }) => (
          <Badge variant="primary" className="narrative-framework-badge">
            {getReportTypeLabel(row.original.framework)}
          </Badge>
        ),
        sortType: 'string',
      },
      {
        Header: t('Status'),
        accessor: 'status',
        Cell: ({ row }) => {
          const status = REPORT_STATUSES[row.original.status] || REPORT_STATUSES.draft;
          return <Badge variant={status.variant}>{status.label}</Badge>;
        },
        sortType: 'string',
      },
      {
        Header: t('Created By'),
        accessor: 'createdBy',
        Cell: ({ row }) => <span className="text-muted">{formatUserName(row.original.createdBy) || '—'}</span>,
        sortType: 'string',
      },
      {
        Header: t('Finalized By'),
        accessor: 'finalizedBy',
        Cell: ({ row }) => <span className="text-muted">{formatUserName(row.original.finalizedBy) || '—'}</span>,
        sortType: 'string',
      },
      {
        Header: t('Created Date'),
        accessor: (row) => new Date(row.createdDate || row.updatedAt),
        Cell: ({ row }) => <span className="text-muted">{formatDate(row.original.createdDate || row.original.updatedAt)}</span>,
        sortType: 'datetime',
      },
      {
        Header: '',
        accessor: 'actions',
        actionColumn: true,
        disableGlobalFilter: true,
        disableSortBy: true,
        Cell: ({ row }) => {
          const report = row.original;
          return (
            <div className="d-flex justify-content-end action-buttons">
              <Button variant="outline-secondary" size="sm" className="mr-2" onClick={() => {
                setIsViewLoading(true);
                getNarrativeReportApi(orgId, report.id)
                  .then((full) => setViewReport(full))
                  .catch(() => { })
                  .finally(() => setIsViewLoading(false));
              }}>
                <IoEyeOutline />
              </Button>
              <Button variant="outline-danger" size="sm" onClick={() => setDeleteReport(report)}>
                <IoTrashOutline />
              </Button>
            </div>
          );
        },
      },
    ],
    [t, orgId]
  );

  return (
    <Container fluid className="p-0 narrative-reports-page narrative-prototype-shell">
      <div className="narrative-list-header">
        <Row>
          <Col>
            <h4 className="mb-2">{t('Narrative Reports')}</h4>
            <p className="text-muted">
              {t('View and edit your climate-related financial disclosure reports')}
            </p>
          </Col>
          <Col className="text-right">
            <Button variant="primary" onClick={() => setShowCreate(true)}>
              <IoAddOutline className="mr-1" />
              {t('Create New Report')}
            </Button>
          </Col>
        </Row>
      </div>

      <div className="narrative-list-main">
        <Card className="mb-3 narrative-filter-card">
          <Card.Body>
            <div className="d-flex align-items-center flex-wrap">
              <div className="d-flex align-items-center mr-3 mb-2">
                <IoFilterOutline className="mr-2 text-muted" />
                <strong>{t('Filters:')}</strong>
              </div>
              <Form.Control as="select" value={frameworkFilter} onChange={(event) => setFrameworkFilter(event.target.value)} className="mr-2 mb-2 narrative-filter-control">
                <option value="all">{t('All Report Types')}</option>
                {REPORT_TYPE_FILTERS.map((framework) => (
                  <option key={framework.key} value={framework.key}>{framework.title} - {framework.name}</option>
                ))}
              </Form.Control>
              <ReactDatePicker
                showMonthYearPicker
                isClearable
                showIcon
                dateFormat="MMM yyyy"
                maxDate={monthStringToDate(endFilter)}
                popperPlacement="bottom-end"
                placeholderText={t('Pick Start Date')}
                selected={monthStringToDate(startFilter)}
                onChange={(value) => setStartFilter(dateToMonthString(value))}
                {...monthPickerClass()}
              />
              <ReactDatePicker
                showMonthYearPicker
                isClearable
                showIcon
                dateFormat="MMM yyyy"
                minDate={monthStringToDate(startFilter)}
                popperPlacement="bottom-end"
                placeholderText={t('Pick End Date')}
                selected={monthStringToDate(endFilter)}
                onChange={(value) => setEndFilter(dateToMonthString(value))}
                {...monthPickerClass()}
              />
              <Form.Control as="select" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} className="mr-2 mb-2 narrative-filter-control">
                <option value="all">{t('All Statuses')}</option>
                {Object.keys(REPORT_STATUSES).filter((key) => key !== 'review').map((key) => (
                  <option key={key} value={key}>{REPORT_STATUSES[key].label}</option>
                ))}
              </Form.Control>
              {(frameworkFilter !== 'all' || statusFilter !== 'all' || startFilter || endFilter) && (
                <Button variant="outline-secondary" className="mb-2 narrative-clear-filter-btn" onClick={clearFilters}>
                  <AiOutlineDelete className="mr-1" />
                  {t('Clear Filters')}
                </Button>
              )}
            </div>
          </Card.Body>
        </Card>

        {!reports && <Spinner animation="grow" />}

        {reports && (
          <Card className="narrative-table-card">
            <DataTable
              columns={columns}
              data={filteredReports}
              defaultSort="Created Date"
              pageLimit={10}
            />
          </Card>
        )}
      </div>

      <CreateNarrativeReportModal
        show={showCreate || !!cloneFramework}
        onClose={() => {
          setShowCreate(false);
          setCloneFramework(null);
          setCloneSourceId(null);
        }}
        onCreate={handleCreate}
        fixedFramework={cloneFramework}
        isLoading={isCreating}
      />
      <ViewReportModal
        show={!!viewReport || isViewLoading}
        report={viewReport}
        isLoading={isViewLoading}
        orgId={orgId}
        onClose={() => setViewReport(null)}
        onEdit={(version) => {
          return viewReport && history.push(`${match.url}/${viewReport.id}${version ? `?auditVersion=${encodeURIComponent(version)}` : ''}`);
        }}
        onClone={() => {
          setCloneFramework(viewReport?.framework);
          setCloneSourceId(viewReport?.id);
          setViewReport(null);
        }}
      />
      <Modal show={!!deleteReport} onHide={() => setDeleteReport(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('Are you absolutely sure?')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t('This action cannot be undone. This will permanently delete the report.')}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setDeleteReport(null)}>{t('Cancel')}</Button>
          <Button variant="danger" onClick={handleDelete}>{t('Delete')}</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default NarrativeReportsTab;
